import { CloudDownloadOutlined, LineChartOutlined } from '@ant-design/icons'
import type { Chart } from '@antv/g2'
import { useDownload } from '@web/react-common'
import { Button, Typography } from 'antd'
import { flatten, groupBy } from 'lodash'
import moment from 'moment'
import React from 'react'

import { PostTagType, StatisticPostTagTypePercentageInfoFragment, useGetStatisticsQuery } from '@/generated/graphql'

import s from '../kpi.module.less'

export const StatisticsPostContentTagTrend: React.FC<{
  range: {
    year?: number
    month?: number
    day?: number
    week?: number
  }
}> = ({ range }) => {
  const title = '帖子分布趋势'
  const divId = 'postContentTagTrendChartDiv'
  const [chart, setChart] = React.useState<Chart>()

  const ref = React.useRef<HTMLDivElement>(null)

  const { data } = useGetStatisticsQuery({
    skip: !range.year && !range.month && !range.day,
    variables: {
      withPostContentTagTrend: true,
      input: {
        year: range.year,
      },
    },
  })

  const compensateWeekData = (year: moment.Moment, array: StatisticPostTagTypePercentageInfoFragment[]) => {
    let weeks = year.weeksInYear()

    const currentWeek = moment().week()

    // 不显示未来日期
    if (moment().year() === year.year()) {
      weeks = currentWeek
    }

    return Array.from({ length: weeks }, (_x, index) => {
      const yearWeek = year
        .clone()
        .week(index + 1)
        .format('YYYY-WW')
      const data = [...(array.find((item) => item.yearWeek === yearWeek)?.data || [])]

      if (data.every((tag) => tag.tag !== PostTagType.WorkProcess)) {
        data.push({ tag: PostTagType.WorkProcess, tagName: '工作动向', count: 0 })
      }
      if (data.every((tag) => tag.tag !== PostTagType.TechShare)) {
        data.push({ tag: PostTagType.TechShare, tagName: '技术分享', count: 0 })
      }
      if (data.every((tag) => tag.tag !== PostTagType.Entertainment)) {
        data.push({ tag: PostTagType.Entertainment, tagName: '生活娱乐', count: 0 })
      }

      return { yearWeek, data }
    })
  }

  const statistics = flatten(
    (data?.statistics.postContentTagTrend
      ? compensateWeekData(moment([range.year!, 1, 1]), data.statistics.postContentTagTrend)
      : []
    ).map((item) => {
      return item.data.map((d) => {
        const [year, week] = item.yearWeek.split('-').map((s: string) => Number(s))
        return {
          week: item.yearWeek,
          tag: d.tagName,
          count: d.count,
          yearMonth: moment().year(year).week(week).format('YYYY-MM'),
        }
      })
    })
  )

  React.useEffect(() => {
    import('../chart.component').then(({ default: Chart }) => {
      const newChart = new Chart({
        container: divId,
        autoFit: true,
        syncViewPadding: true,
      })
      newChart.animate(false)
      setChart(newChart)
    })
  }, [])

  React.useEffect(() => {
    if (statistics && chart) {
      chart.clear()
      chart.data(statistics)
      chart.scale({
        week: {
          range: [0, 1],
        },
        count: {
          nice: true,
        },
      })

      chart.tooltip({
        showCrosshairs: true,
        shared: true,
        title: (_, data) => moment(data.yearMonth).format(`YYYY [年] MM [月] (${data.week.split('-')[1]} [周])`),
      })

      chart.axis('week', {
        tickLine: {
          alignTick: true,
          length: 6,
        },
      })

      chart.line().position('week*count').color('tag').shape('line')

      chart.point().position('week*count').color('tag')

      chart.render()
    }
  }, [chart, statistics])

  const groupedStatistics = groupBy(statistics, 'week')
  const file = [['日期 (周)', '工作动向', '技术分享', '生活娱乐']].concat(
    statistics.length > 0
      ? Object.keys(groupedStatistics).map((key) => [
          key,
          (groupedStatistics[key].find((data) => data.tag === '工作动向')?.count || 0).toString(),
          (groupedStatistics[key].find((data) => data.tag === '技术分享')?.count || 0).toString(),
          (groupedStatistics[key].find((data) => data.tag === '生活娱乐')?.count || 0).toString(),
        ])
      : [['', '']]
  )

  const fileName = `Tale${title} - ${range.year}.csv`

  const header = (title: string) => (
    <div style={{ display: 'flex', fontSize: 32, margin: '16px 0 8px 0', justifyContent: 'center' }}>
      <LineChartOutlined />
      <Typography.Title style={{ marginLeft: 8 }} level={3}>
        {title}
      </Typography.Title>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Button icon={<CloudDownloadOutlined />} type='link' onClick={() => useDownload(file, fileName)}>
          下载
        </Button>
      </div>
    </div>
  )

  return (
    <div className={s.chartContainer}>
      {header(title)}
      <div className={s.chartBox}>
        <div id={divId} className={s.chart} ref={ref} />
      </div>
    </div>
  )
}
